import React, {forwardRef, PropsWithChildren, ReactNode, Ref} from 'react';
import styled, {css, DefaultTheme} from 'styled-components';

import {buildHoverParentClassName} from '../../../../libs/shared/core/src/helpers/browser/hoverHelpers';
import {
  forwardInteractionComponentProps,
  InteractionComponentProps,
} from '../../../../libs/shared/core/src/helpers/interaction/interactionHelpers';
import {EventHandler, Renderer} from '../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {blendColors} from '../../../../libs/shared/core/src/models/helpers/colorHelpers';
import {ChromelessButton} from '../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {BlockLink} from '../../../../libs/shared/ui/src/components/links/blockLink';
import {TooltipCoordinator} from '../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {startPerformanceInteraction} from '../../../../srcCommon/services/performanceMetrics/interactionPerformance';
import {PerformanceInteractionTypesEnum} from '../../../../srcCommon/services/performanceMetrics/interactionPerformanceTypes';
import {cellsFetchPerformanceId} from '../../../services/performanceMetrics/performanceIdHelpers';
import {DropLinePositionsEnum} from '../../inboxes/workspaces/dnd/workspaceDragDropTypes';
import {maybeAddWorkspaceDropLineStyle} from '../../inboxes/workspaces/line/workspaceLineStyles';
import {StyledChevronButton} from './navigationLineChevron';

/*
 * Props.
 */

export interface NavigationLineProps extends InteractionComponentProps {
  /** Is the navigation line selected (e.g. an inbox item being selected with its conversations visible in P2/P3). Not relevant to edit mode. */
  isSelected?: boolean;
  /** Is the navigation line being hovered (used by the drag'n'drop code) */
  hasHover?: boolean;
  /** Indent this line to the right. */
  indentLevel?: number;
  /** Click event handler. */
  onClick?: EventHandler;
  /** Render a tooltip for this line. */
  renderTooltip?: Renderer;
  /** Whether to prevent a hover state. */
  disableBackground?: boolean;
  /** Whether this line is disabled (for offline mode). */
  isDisabled?: boolean;
  /** Disabling will not affect opacity (but will otherwise work as normal).  */
  isOpaque?: boolean;
  /** Skips adding the disabled pointer styling(useful for when you want a tooltip, but clickable) */
  ignoreDisabledPointerStyling?: boolean;
  /** Whether this line is being dragged. */
  isDragging?: boolean;
  /** Location to navigate to when clicked. */
  to?: string;
  previewRefCallback?: React.MutableRefObject<HTMLDivElement | null>;
  /** Whether conversations are ready to be dropped on this line. */
  isDropping?: boolean;
}

/*
 * Style.
 */

export const navigationLineChevronWidth = 16;
export const navigationLineIndentPaddingFactor = 18;
export const navigationLineIndentPaddingOffset = 6;
export const navigationLineMaximumIndentLevel = 3;

interface LineStyleProps {
  $isDisabled?: boolean;
  $isOpaque?: boolean;
  $isDropping?: boolean;
  $isDragging?: boolean;
  $disableBackground?: boolean;
  $isSelected?: boolean;
  $indentLevel?: number;
  $hasHover?: boolean;
  $dropLine?: DropLinePositionsEnum;
  $ignoreDisabledPointerStyling?: boolean;
}
const StyledLineDiv = styled.div<LineStyleProps>`
  height: 28px;
  padding: 3px 5px 3px 6px;
  margin: 0 5px;

  display: grid;
  grid-template-columns: ${navigationLineChevronWidth}px 22px 4px 1fr 4px auto;
  grid-template-areas: 'chevron icon . label . right';

  opacity: ${(p) => (p.$isDisabled && !p.$isOpaque ? '0.5' : '1')};
  border-radius: 4px;

  ${(p) => maybeAddDropBorderStyle(p)};
  ${(p) => addBackgroundStyle(p, p.theme)};
  ${(p) => addPaddingLeftStyles(p)};
  ${(p) => maybeAddDisabledStyle((p.$isDisabled && !p.$ignoreDisabledPointerStyling) || false)}
  ${(p) =>
    maybeAddWorkspaceDropLineStyle({
      indentLevel: p.$indentLevel,
      chevronWidth: navigationLineChevronWidth,
      indentPaddingFactor: navigationLineIndentPaddingFactor,
      maximumIndentLevel: navigationLineMaximumIndentLevel,
    })};
  ${(p) => maybeAddDraggingStyle(p.$isDragging)};
  position: relative;
`;

function maybeAddDraggingStyle(isDragging?: boolean) {
  if (!isDragging) {
    return '';
  }

  return css`
    padding-left: 6px;
    margin-left: 0;
    margin-right: 0;
    grid-template-columns: 22px 4px 1fr 4px auto;
    grid-template-areas: 'icon . label . right';

    ${StyledChevronButton} {
      display: none;
    }
  `;
}

function maybeAddDropBorderStyle(props: LineStyleProps) {
  if (!props.$isDropping) {
    return '';
  }

  return css`
    &:not([data-line-position]),
    &[data-line-position='${DropLinePositionsEnum.NONE}'] {
      box-shadow:
        0 0 0 1px ${(p) => p.theme.palette.blue.shade40},
        inset 0 0 0 1px ${(p) => p.theme.palette.blue.shade40};
      z-index: 1;
    }
  `;
}

function addBackgroundStyle(props: LineStyleProps, theme: DefaultTheme) {
  /** We may not need to show any background. */
  if (props.$disableBackground) {
    return '';
  }

  // If we're selected, we need a fixed background color.
  if (props.$isSelected) {
    return css`
      background-color: ${blendColors(theme.leftBar.background, theme.leftBar.itemBackgroundSelected)};
    `;
  }

  if (props.$hasHover) {
    return css`
      background-color: ${blendColors(theme.leftBar.background, theme.leftBar.itemBackgroundHover)};
    `;
  }

  // Otherwise, return the hover logic.
  return css`
    background-color: ${theme.leftBar.background};
    &:hover {
      background-color: ${blendColors(theme.leftBar.background, theme.leftBar.itemBackgroundHover)};
    }
  `;
}

function addPaddingLeftStyles({$indentLevel}: LineStyleProps) {
  const indentMultiplier = Math.min($indentLevel || 0, navigationLineMaximumIndentLevel);
  const indentPadding = indentMultiplier * navigationLineIndentPaddingFactor;
  return css`
    padding-left: ${indentPadding + navigationLineIndentPaddingOffset}px;
  `;
}

function maybeAddDisabledStyle(isDisabled: boolean) {
  if (!isDisabled) {
    return '';
  }

  return css`
    pointer-events: none;
  `;
}

/*
 * Component.
 */

/** Common wrapper for a single line of a P1 navigation. */
export const NavigationLine = forwardRef<HTMLDivElement, PropsWithChildren<NavigationLineProps>>(
  (props, ref) => {
    const content = (
      <StyledLineDiv
        $isDisabled={props.isDisabled}
        $isOpaque={props.isOpaque}
        $isDragging={props.isDragging}
        $isDropping={props.isDropping}
        $disableBackground={props.disableBackground}
        $isSelected={props.isSelected}
        $hasHover={props.hasHover}
        $indentLevel={props.indentLevel}
        $ignoreDisabledPointerStyling={props.ignoreDisabledPointerStyling}
        className={buildHoverParentClassName(props.hasHover)}
        ref={props.previewRefCallback}
      >
        {props.children}
      </StyledLineDiv>
    );

    const control = props.to ? renderLink(ref, props.to, props, content) : renderButton(ref, props, content);

    return <TooltipCoordinator render={props.renderTooltip}>{control}</TooltipCoordinator>;
  },
);

function renderButton(ref: Ref<HTMLDivElement>, props: NavigationLineProps, content: ReactNode) {
  return (
    <ChromelessButton
      ref={ref}
      onClick={props.onClick}
      isDisabled={props.isDisabled}
      {...forwardInteractionComponentProps(props)}
    >
      {content}
    </ChromelessButton>
  );
}

function renderLink(ref: Ref<HTMLDivElement>, to: string, props: NavigationLineProps, content: ReactNode) {
  if (props.isDisabled) {
    return renderButton(ref, props, content);
  }

  return (
    <BlockLink
      // Disabling because there's no good way to check the type of the ref at a higher-level.
      // Feel free to fix it if you think I'm wrong :P
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      ref={ref as any}
      to={to}
      onClick={(event) => {
        if (props.onClick) {
          props.onClick(event);
        }

        const performanceId = startPerformanceInteraction(event, PerformanceInteractionTypesEnum.CELLS_FETCH);
        if (!performanceId) {
          return;
        }

        cellsFetchPerformanceId.set(performanceId);
      }}
      {...forwardInteractionComponentProps(props)}
    >
      {content}
    </BlockLink>
  );
}
